import { DataGrid, type GridColumn } from "@/components/data-grid";
import { EmptyState } from "@/components/EmptyState";
import { PlusIcon } from "@/components/icons";
import { Alert, AlertDescription, AlertTitle } from "@/components/ui/alert";
import { Button } from "@/components/ui/button";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from "@/components/ui/dialog";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { useState, type ComponentType } from "react";

import { errorMessage } from "../lib/query.ts";
import { analytics } from "../lib/telemetry/analytics.ts";
import type { WorkSection } from "../lib/telemetry/events.ts";

import { useSession } from "../app/session.tsx";
import {
  canSaveCatalogEntry,
  catalogQuery,
  isValidCode,
  type Catalog,
  type CatalogEntry,
  type CatalogInput,
} from "./catalog.ts";

/**
 * La pantalla de un catálogo: cargos o regiones, la misma.
 *
 * Comparten pantalla porque comparten forma --código, nombre y cuántas
 * vinculaciones los usan--, no por ahorrar archivos. Lo que las distingue son
 * las palabras y las herramientas del backend, y eso viaja en el `Catalog`.
 *
 * La columna de vinculaciones no es informativa: es la que dice si borrar es
 * gratis. Un cargo con vinculaciones no se puede borrar y el servidor lo
 * rechaza; enseñar el número antes evita pedir algo que va a fallar.
 */
const CATALOG_COLUMNS: readonly GridColumn<CatalogEntry>[] = [
  { key: "name", label: "Nombre", render: (entry) => entry.name },
  {
    key: "code",
    label: "Código",
    render: (entry) => <span className="font-mono text-sm">{entry.code}</span>,
  },
  {
    key: "enrollments",
    label: "Vinculaciones",
    numeric: true,
    render: (entry) => entry.enrollments.toLocaleString("es"),
  },
  {
    key: "createdAt",
    label: "Creado",
    render: (entry) => dayOf(entry.createdAt),
  },
];

/** La fecha en que se creó, sin la hora: aquí nadie mira los minutos. */
const dayOf = (moment: string): string =>
  new Date(moment).toLocaleDateString("es");

type Editing =
  | { readonly kind: "new" }
  | { readonly kind: "edit"; readonly entry: CatalogEntry }
  | { readonly kind: "delete"; readonly entry: CatalogEntry };

/** La sección de Work de un catálogo, para la telemetría. */
const sectionOf = (catalog: Catalog): WorkSection =>
  catalog.queryKey[0] === "regions" ? "regions" : "positions";

export function CatalogPanel({
  catalog,
  icon,
}: {
  readonly catalog: Catalog;
  /**
   * El icono lo pone quien monta la pantalla y no el descriptor, por lo mismo
   * que en la barra lateral del panel: el catálogo dice qué hay y con qué
   * herramientas se toca, no cómo se dibuja.
   */
  readonly icon: ComponentType<{ className?: string }>;
}) {
  const list = useQuery(catalogQuery(catalog));
  const [editing, setEditing] = useState<Editing | null>(null);
  /* Sin permiso de escritura el catálogo se lee y ya está: ni botón de
     nuevo ni ficha que abrir, porque todo lo que hay en la ficha es editar. */
  const canWrite = useSession().can(catalog.writePermission);

  const entries = list.data?.entries ?? [];
  const total = list.data?.total ?? 0;
  const { copy } = catalog;

  const close = () => {
    setEditing(null);
  };

  return (
    <section className="flex min-h-0 flex-1 flex-col gap-4 overflow-y-auto p-4">
      {/* Sin titular: quién es esta pantalla lo dice la cabecera de arriba. */}
      <header className="flex flex-wrap items-end justify-between gap-3">
        <div>
          <p className="text-sm text-muted-foreground">
            {list.isPending
              ? "Cargando…"
              : total === 1
                ? "1 registro"
                : `${total.toLocaleString("es")} registros`}
          </p>
        </div>

        {canWrite && (
          <Button
            onClick={() => {
              analytics.work.recordCreateStarted({
                section: sectionOf(catalog),
              });
              setEditing({ kind: "new" });
            }}
          >
            <PlusIcon /> {copy.newAction}
          </Button>
        )}
      </header>

      <p className="text-sm text-muted-foreground">{copy.subtitle}</p>

      {list.error != null && (
        <Alert variant="destructive">
          <AlertTitle>No se pudo cargar</AlertTitle>
          <AlertDescription>{errorMessage(list.error)}</AlertDescription>
        </Alert>
      )}

      {!list.isPending && list.error == null && (
        <DataGrid
          columns={CATALOG_COLUMNS}
          rows={entries}
          empty={<EmptyState icon={icon}>{copy.empty}</EmptyState>}
          {...(canWrite
            ? {
                onRowClick: (entry: CatalogEntry) => {
                  analytics.work.recordOpened({
                    section: sectionOf(catalog),
                    recordId: entry.id,
                    code: entry.code,
                    name: entry.name,
                  });
                  setEditing({ kind: "edit", entry });
                },
              }
            : {})}
        />
      )}

      {editing?.kind === "new" && (
        <EntryDialog catalog={catalog} entry={null} onClose={close} />
      )}

      {editing?.kind === "edit" && (
        <EntryDialog
          catalog={catalog}
          entry={editing.entry}
          onClose={close}
          onDelete={() => {
            setEditing({ kind: "delete", entry: editing.entry });
          }}
        />
      )}

      {editing?.kind === "delete" && (
        <DeleteDialog
          catalog={catalog}
          entry={editing.entry}
          onClose={close}
          onDeleted={close}
        />
      )}
    </section>
  );
}

/**
 * El alta y la edición, que son el mismo formulario.
 *
 * Se monta al abrirlo y no vive oculto: así nace siempre con lo que hay
 * guardado ahora, y no con lo que alguien escribió y canceló hace un rato.
 */
function EntryDialog({
  catalog,
  entry,
  onClose,
  onDelete,
}: {
  readonly catalog: Catalog;
  /** `null` es el alta. */
  readonly entry: CatalogEntry | null;
  onClose: () => void;
  onDelete?: () => void;
}) {
  const queryClient = useQueryClient();
  const [draft, setDraft] = useState<CatalogInput>({
    code: entry?.code ?? "",
    name: entry?.name ?? "",
  });

  const save = useMutation({
    mutationFn: (input: CatalogInput) =>
      entry === null ? catalog.create(input) : catalog.update(entry.id, input),
    onSuccess: async (_result, input) => {
      analytics.work.recordSaved({
        section: sectionOf(catalog),
        mode: entry === null ? "create" : "update",
        ...(entry === null ? {} : { recordId: entry.id }),
        code: input.code,
        name: input.name,
      });
      await queryClient.invalidateQueries({ queryKey: catalog.queryKey });
      onClose();
    },
  });

  const canSave = canSaveCatalogEntry(draft) && !save.isPending;
  const codeIsWrong = draft.code !== "" && !isValidCode(draft.code);

  return (
    <Dialog open onOpenChange={onClose}>
      <DialogContent>
        <form
          className="grid gap-4"
          onSubmit={(event) => {
            event.preventDefault();
            if (canSave) save.mutate(draft);
          }}
        >
          <DialogHeader>
            <DialogTitle>
              {entry === null ? catalog.copy.newAction : catalog.copy.editTitle}
            </DialogTitle>
            <DialogDescription>
              El código es con lo que se nombra al mapear la columna de un
              archivo; el nombre es lo que se lee en las pantallas. Cambiar
              cualquiera de los dos no toca las vinculaciones: apuntan al
              registro, no a su texto.
            </DialogDescription>
          </DialogHeader>

          <div className="grid gap-1.5">
            <Label htmlFor="catalog-name">Nombre</Label>
            <Input
              id="catalog-name"
              value={draft.name}
              autoFocus
              onChange={(event) => {
                setDraft({ ...draft, name: event.target.value });
              }}
            />
            <p className="text-xs text-muted-foreground">
              {catalog.copy.nameHint}
            </p>
          </div>

          <div className="grid gap-1.5">
            <Label htmlFor="catalog-code">Código</Label>
            <Input
              id="catalog-code"
              className="font-mono"
              value={draft.code}
              aria-invalid={codeIsWrong}
              onChange={(event) => {
                setDraft({ ...draft, code: event.target.value });
              }}
            />
            <p className="text-xs text-muted-foreground">
              {catalog.copy.codeHint}
            </p>
          </div>

          {save.isError && (
            <Alert variant="destructive">
              <AlertDescription>{errorMessage(save.error)}</AlertDescription>
            </Alert>
          )}

          <DialogFooter className="sm:justify-between">
            {onDelete === undefined ? (
              <span />
            ) : (
              <Button type="button" variant="ghost" onClick={onDelete}>
                Borrar
              </Button>
            )}

            <div className="flex gap-2">
              <Button type="button" variant="ghost" onClick={onClose}>
                Cancelar
              </Button>
              <Button type="submit" disabled={!canSave}>
                {save.isPending ? "Guardando…" : "Guardar"}
              </Button>
            </div>
          </DialogFooter>
        </form>
      </DialogContent>
    </Dialog>
  );
}

/**
 * El borrado.
 *
 * Nombra lo que se va a borrar y, si hay vinculaciones colgando, lo dice
 * ANTES: el servidor lo rechazará, y enterarse por un error es peor que
 * enterarse por el propio diálogo.
 */
function DeleteDialog({
  catalog,
  entry,
  onClose,
  onDeleted,
}: {
  readonly catalog: Catalog;
  readonly entry: CatalogEntry;
  onClose: () => void;
  onDeleted: () => void;
}) {
  const queryClient = useQueryClient();

  const remove = useMutation({
    mutationFn: () => catalog.remove(entry.id),
    onSuccess: async () => {
      analytics.work.recordDeleted({
        section: sectionOf(catalog),
        inUse: entry.enrollments > 0,
        recordId: entry.id,
        code: entry.code,
        name: entry.name,
      });
      await queryClient.invalidateQueries({ queryKey: catalog.queryKey });
      onDeleted();
    },
  });

  const inUse = entry.enrollments > 0;

  return (
    <Dialog open onOpenChange={onClose}>
      <DialogContent>
        <DialogHeader>
          <DialogTitle>{catalog.copy.deleteQuestion(entry.name)}</DialogTitle>
          <DialogDescription>
            {inUse
              ? `${catalog.copy.inUse(entry.enrollments)} Mientras las haya, el servidor no deja borrarlo.`
              : "No lo usa ninguna vinculación. No se puede deshacer."}
          </DialogDescription>
        </DialogHeader>

        {remove.isError && (
          <Alert variant="destructive">
            <AlertDescription>{errorMessage(remove.error)}</AlertDescription>
          </Alert>
        )}

        <DialogFooter>
          <Button variant="ghost" onClick={onClose}>
            Cancelar
          </Button>
          <Button
            variant="destructive"
            disabled={inUse || remove.isPending}
            onClick={() => {
              remove.mutate();
            }}
          >
            {remove.isPending ? "Borrando…" : "Borrar"}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
